import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, Plus, RefreshCw } from 'lucide-react';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { StatTile } from '@/components/ui/stat-tile';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/features/auth/auth.store';
import { formatCurrency, formatNumber } from '@/lib/format';
import { storeToday } from '@/lib/store-time';
import { CardsTab } from './cards-tab';
import { CreateCardsDialog } from './create-cards-dialog';
import { GiftCardDetailSheet, type CardRef } from './gift-card-detail-sheet';
import { PurchasesTab, TransactionsTab } from './history-tabs';
import { useAccountPayable } from './gift-cards.api';
import type { DateRange } from './gift-cards.api';

/**
 * Gift cards, as one page.
 *
 * The original was two menu entries — Admin Services > Gift Cards and
 * Reports > Gift Cards — pointing at two components that called the same two
 * endpoints with the same payload and differed by a single "Create" button.
 * The split was a role workaround: the admin sidebar was gated on SuperAdmin
 * and the reports sidebar on `Finance || TopAnalytics`, so neither audience
 * could see the other's copy, and the pages themselves enforced nothing.
 *
 * Here it is one page under Finance, and Create is an action gated on the role
 * that was meant to hold it.
 *
 * The three tabs answer three different questions and are deliberately not
 * merged: Cards is "what exists and what is left on it", Transactions is
 * "everything that moved, both directions", Purchases is "money on only, by
 * store". The last two look alike but come from different endpoints with
 * different columns.
 */

/** Both list tabs open on the current month, which is what a reconciler wants. */
function currentMonth(): DateRange {
  const today = storeToday();
  return { start: `${today.slice(0, 7)}-01`, end: today };
}

export function GiftCardsPage() {
  const { t } = useTranslation();
  const canCreate = useAuthStore((s) => s.can('isSuperAdmin'));

  const [creating, setCreating] = useState(false);
  const [openCard, setOpenCard] = useState<CardRef | null>(null);
  const [dates, setDates] = useState<DateRange>(currentMonth);

  const payable = useAccountPayable();

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('giftCards.title')}
        description={t('giftCards.subtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('common.reload')}
              disabled={payable.isFetching}
              onClick={() => void payable.refetch()}
            >
              <RefreshCw className={payable.isFetching ? 'animate-spin' : ''} />
            </Button>
            {canCreate && (
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus />
                <span className="hidden sm:inline">
                  {t('giftCards.createCards')}
                </span>
              </Button>
            )}
          </>
        }
      />

      {/* The liability, which is the number finance opens this page for: what
          is still sitting unspent on activated cards. */}
      <div className="grid grid-cols-2 gap-2">
        <StatTile
          label={t('giftCards.accountPayable')}
          value={formatCurrency(payable.data?.accountPayable ?? 0)}
          sub={t('giftCards.accountPayableHint')}
          tone="cash"
          loading={payable.isPending}
          busy={payable.isFetching}
        />
        <StatTile
          label={t('giftCards.activeCards')}
          value={formatNumber(payable.data?.totalActiveGiftCards ?? 0)}
          sub={t('giftCards.activeCardsHint')}
          loading={payable.isPending}
          busy={payable.isFetching}
        />
      </div>

      {payable.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <p>{t('giftCards.payableFailed')}</p>
        </div>
      )}

      <Tabs defaultValue="cards">
        <TabsList>
          <TabsTrigger value="cards">{t('giftCards.tabCards')}</TabsTrigger>
          <TabsTrigger value="transactions">
            {t('giftCards.tabTransactions')}
          </TabsTrigger>
          <TabsTrigger value="purchases">
            {t('giftCards.tabPurchases')}
          </TabsTrigger>
        </TabsList>

        {/* The range is shared by the two ledger tabs, so switching between
            them keeps the period a reader has already set. Cards has no date
            filter — a card is looked up by code, not by when it was made. */}
        <TabsContent value="cards">
          <CardsTab onOpen={setOpenCard} />
        </TabsContent>
        <TabsContent value="transactions">
          <TransactionsTab
            dates={dates}
            onDatesChange={setDates}
            onOpen={setOpenCard}
          />
        </TabsContent>
        <TabsContent value="purchases">
          <PurchasesTab
            dates={dates}
            onDatesChange={setDates}
            onOpen={setOpenCard}
          />
        </TabsContent>
      </Tabs>

      <GiftCardDetailSheet
        card={openCard}
        open={openCard !== null}
        onOpenChange={(next) => {
          if (!next) setOpenCard(null);
        }}
      />

      <CreateCardsDialog open={creating} onOpenChange={setCreating} />
    </div>
  );
}
